export interface OfferView {
  readonly bookmaker: string;
  readonly price: number;
  readonly line: number | null;
  readonly role: "AU_EXECUTION" | "US_REFERENCE";
  readonly url: string | null;
  /** MILESTONE when the price is the book's N+ milestone, not its Over line. */
  readonly product?: "LINE" | "MILESTONE";
}

export interface RecommendationView {
  readonly sport?: DashboardSport;
  readonly executionMode?: "LIVE" | "TEST_ONLY" | "TRACK_ONLY";
  readonly signalId: string;
  /** Stable key the worker alerts on, used to deep-link a bet from Discord. */
  readonly naturalKey?: string;
  readonly melbourneTip: string;
  /** First pitch as an instant. The home board sorts by how soon a bet has to
   * be placed and counts down to it, which a formatted time cannot answer. */
  readonly tipUtc?: string;
  readonly ruleVersion: string;
  /** Readable rule name from the API. The dashboard carries no workspace
   * dependency, so the naming lives server-side. */
  readonly ruleName?: string;
  /** The quote shown is the one the bet was recommended at, because no live
   * offer was on hand. Normal between odds pulls. */
  readonly priceIsStale?: boolean;
  readonly description: string;
  readonly gameLabel: string;
  readonly bookmaker: string;
  readonly line: number | null;
  readonly price: number;
  readonly minimumOdds: number;
  readonly units: number;
  readonly stakeCents: number;
  readonly status: "RECOMMENDED" | "BELOW_VALUE" | "AWAITING_LINEUP";
  readonly evidence: readonly string[];
  readonly offers: readonly OfferView[];
}

export interface DashboardSnapshot {
  readonly bankrollCents: number;
  readonly unitCents: number;
  readonly committedCents: number;
  readonly recommendedCents: number;
  readonly replayReady: boolean;
  readonly recommendations: readonly RecommendationView[];
}

export interface ProviderUsageView {
  readonly used: number;
  readonly normalLimit: number;
  readonly hardLimit: number;
  readonly remaining: number;
  readonly optionalSamplingStopped: boolean;
  readonly betCriticalExhausted: boolean;
  readonly lastSuccessAt: string | null;
  readonly lastFailureAt: string | null;
  readonly projectedExhaustionAt: string | null;
  readonly providerReportedLimit?: number | null;
  readonly providerReportedRemaining?: number | null;
  readonly allowanceMismatch?: boolean;
  /** When this counter goes back to zero. The two providers do not reset
   * together, and neither resets at Melbourne midnight. */
  readonly windowEndsAt?: string | null;
}

export interface HealthRollupRow {
  readonly sport: string;
  readonly component: string;
  readonly severity:
    "BROKEN" | "STALE" | "DEGRADED" | "OUT_OF_SEASON" | "HEALTHY";
  /** What the component last said about itself, before grading. */
  readonly reportedStatus?: string;
  readonly detail: string;
  readonly neverSucceeded: boolean;
  readonly lastAttemptAt: string | null;
  readonly lastSuccessAt: string | null;
}

export interface HealthView {
  readonly sport: DashboardSport;
  readonly releaseState: "ACTIVE" | "INACTIVE" | "NOT_CONFIGURED";
  readonly live: boolean;
  readonly ready: boolean;
  readonly replayReady: boolean;
  readonly components: readonly {
    readonly sport: DashboardSport;
    readonly component: string;
    readonly label: string;
    readonly detail: string;
    readonly releaseState: "ACTIVE" | "INACTIVE" | "NOT_CONFIGURED";
    readonly requiredForRelease: boolean;
    readonly status:
      "HEALTHY" | "DEGRADED" | "UNHEALTHY" | "STARTING" | "INACTIVE";
  }[];
  readonly usage?: {
    readonly highlightly: ProviderUsageView;
    readonly rapidodds: ProviderUsageView & {
      readonly budgetMode: "TRIAL" | "STARTER";
      readonly dailyUsed: number;
      readonly dailyLimit: number;
      readonly allowanceUsed: number;
      readonly allowanceLimit: number;
    };
  };
  readonly localAgent?: {
    readonly status: "ONLINE" | "OFFLINE" | "NOT_ENROLLED";
    readonly displayName: string | null;
    readonly lastSeenAt: string | null;
    readonly pendingJobs: number;
  };
  readonly systemHealth?: readonly HealthRollupRow[];
}

export interface PendingLineupView {
  readonly candidateId: string;
  readonly gameId: string;
  readonly playerId: string;
  readonly playerName: string;
  readonly gameLabel: string;
  readonly melbourneTip: string;
  readonly ruleVersion: string;
  readonly units: number;
}

/** One bookmaker account: funds, wear, and how replaceable it is. */
export interface BookExposureView {
  readonly bookmaker: string;
  readonly status: "ACTIVE" | "LIMITED" | "CLOSED";
  readonly availableCents: number;
  readonly pendingCents: number;
  readonly turnoverCents: number;
  readonly turnoverShare: number;
  readonly profitCents: number;
  readonly roiPct: number | null;
  readonly bets: number;
  readonly scarcity: number;
  readonly lowBalance: boolean;
  readonly pricesFrom?: string | null;
  readonly avgDailyStakeCents?: number;
  readonly peakDailyStakeCents?: number;
  readonly peakDay?: string | null;
  readonly targetCents?: number;
}

/** The accountant's transfers: withdraw from one book, deposit into another. */
export interface FundingPlanView {
  readonly moves: readonly {
    readonly from: string;
    readonly to: string;
    readonly amountCents: number;
  }[];
  readonly shortfallCents: number;
}

export interface LedgerRowView {
  readonly betId: string;
  /** "Away @ Home", sent for rows with a live tracker reading. */
  readonly matchup?: string | null;
  /** Which sport, so a combined ledger can read each row's own clock. */
  readonly sport?: string;
  /** LIVE stakes money; TEST_ONLY and TRACK_ONLY are bench rules that stake
   * nothing, so they never move a bankroll curve. */
  readonly executionMode?: "LIVE" | "TEST_ONLY" | "TRACK_ONLY" | null;
  /** First pitch. Without it a bet cannot be called late, only unstarted. */
  readonly gameStartsAt?: string | null;
  /** Which feed graded it. Highlightly is the five-hour backup and its box
   * score can lag, so a bet graded from it is worth seeing. */
  readonly settlementSource?: string | null;
  /** SENT, WITHDRAWN, WITHDRAWN_PENDING. A withdrawn alert means the system
   * has retracted a bet the operator may already have been told to place. */
  readonly alertStatus?: string | null;
  readonly loggedAfterTheFact?: boolean;
  readonly signalId: string;
  readonly placedAt: string;
  /** When the bet resolved. Absent while it is still open, and absent on
   * older rows that were settled before this was recorded. */
  readonly settledAt?: string | null;
  readonly ruleVersion: string;
  readonly description: string;
  readonly selection?: string;
  /** PLAYER_3PM and the like, for the live win chance. */
  readonly market?: string;
  /** When Moss confirmed a voided bet's stake came back. */
  readonly refundConfirmedAt?: string | null;
  /** Minutes so far in a live game; 0 deep into it means a likely void. */
  readonly liveMinutes?: number | null;
  readonly gameLabel: string;
  readonly bookmaker: string;
  readonly actualLine: number | null;
  readonly actualPrice: number;
  readonly stakeCents: number;
  readonly units: number;
  readonly status: "PLACED" | "SETTLED" | "VOID";
  readonly result: "WIN" | "LOSS" | "PUSH" | "VOID" | null;
  /** The final stat the bet was graded on. */
  readonly actualValue?: number | null;
  /** Why it won or lost, in words ("Shot 0-7 from three; needed 1."). */
  readonly resultReason?: string | null;
  /** NBA bets in play: foul trouble, shooting so far, blowout risk. */
  readonly liveNote?: string | null;
  readonly profitUnits: number | null;
  readonly closingLine: number | null;
  readonly closingPrice: number | null;
  readonly lineClv: number | null;
  readonly priceClv: number | null;
  readonly fallbackSnapshotLine?: number | null;
  readonly fallbackSnapshotPrice?: number | null;
  readonly fallbackSnapshotWindow?: string | null;
  readonly fallbackSnapshotObservedAt?: string | null;
  /** Last scheduled snapshot before this game started. Deliberately not called
   * a close: it is the final pre-tip price we saw, not the bookmaker's. */
  readonly lastPretipLine?: number | null;
  readonly lastPretipPrice?: number | null;
  readonly lastPretipSnapshotAt?: string | null;
  readonly lastPretipSourceAt?: string | null;
  readonly pretipLineMovement?: number | null;
  readonly pretipPriceMovement?: number | null;
  readonly pretipSnapshotStatus?: "EXACT" | "LINE_MOVED" | "UNAVAILABLE" | null;
  readonly synthetic: boolean;
  readonly liveProgress?: {
    readonly status: "IN_PROGRESS" | "FINAL";
    readonly actualValue: number | null;
    readonly homeScore: number | null;
    readonly awayScore: number | null;
    /** MLB only. Null for the other sports, which have no innings. */
    /** Quarter or period in play, and seconds left in it. */
    readonly period: number | null;
    readonly clockSeconds: number | null;
    readonly inningsPlayed: number | null;
    readonly inningHalf: "TOP" | "BOTTOM" | null;
    /** Opportunity already spent - at-bats for a batter prop. Null where the
     * market has no honest denominator, which includes pitcher strikeouts. */
    readonly opportunityValue: number | null;
    readonly opportunityLabel: string | null;
    /** MLB lineup reading from the live feed; null elsewhere. */
    readonly outs?: number | null;
    readonly battingSlot?: number | null;
    readonly battersAway?: number | null;
    readonly teamBatting?: boolean | null;
    readonly playerRemoved?: boolean;
    /** Set once the outcome is certain, before the bet settles. */
    readonly expectedResult?: "WIN" | "LOSS" | null;
    readonly observedAt: string;
  } | null;
}

export interface BacklogItemView {
  readonly id: string;
  readonly createdAt: string;
  readonly title: string;
  readonly note: string | null;
  readonly status: "OPEN" | "DOING" | "DONE" | "DROPPED";
  readonly resolutionNote: string | null;
}

export type DashboardSport = "NBA" | "NHL" | "NFL" | "MLB" | "SOCCER" | "NBL";

function requestFailureMessage(
  status: number,
  body: Record<string, unknown> | null,
): string {
  const code = typeof body?.code === "string" ? body.code : null;
  if (code === "BELOW_MINIMUM_ODDS") {
    const minimum = body?.minimumOdds;
    return typeof minimum === "number"
      ? `The entered price is below the minimum acceptable odds (${minimum.toFixed(2)}).`
      : "The entered price is below the minimum acceptable odds.";
  }
  const messages: Record<string, string> = {
    RISK_CAP_EXCEEDED: "Placement blocked by the current risk cap.",
    LINE_MISMATCH: "The entered line does not match the recommended line.",
    PRICE_MISMATCH: "The entered price does not match the available quote.",
    INVALID_STAKE: "The stake must not exceed the recommended amount.",
    GAME_STARTED: "The game has already started, so this bet cannot be logged.",
    CONTEXT_UNAVAILABLE:
      "The recommendation details are temporarily unavailable. Refresh and try again.",
    STALE_OR_UNAVAILABLE:
      "The stored market quote is unavailable. Refresh and try again.",
    REFERENCE_ONLY:
      "DraftKings and Pinnacle are reference-only and cannot receive a bet.",
    NOT_FOUND: "This recommendation is no longer available.",
  };
  return code === null
    ? `The placement request failed (${status}). Please try again.`
    : (messages[code] ?? `The placement was rejected (${code}).`);
}

function sportPath(path: string, sport: DashboardSport): string {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}sport=${encodeURIComponent(sport)}`;
}

import type { AnalyticsRow } from "../pages/analytics.js";
import type { RuleHealthView } from "../pages/rules.js";
import type { DayWindowView } from "../pages/calendar.js";
import type { RuleHistory } from "../components/rule-panel.js";

export class DashboardApi {
  constructor(
    private readonly baseUrl: string,
    private readonly token: () => string | null,
    private readonly onUnauthorized: () => void = () => undefined,
  ) {}

  async request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = this.token();
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        ...init?.headers,
      },
    });
    if (response.status === 401) this.onUnauthorized();
    const body = (await response.json().catch(() => null)) as Record<
      string,
      unknown
    > | null;
    if (!response.ok)
      throw new Error(requestFailureMessage(response.status, body));
    return body as T;
  }

  /** Registers this phone for bet alerts. */
  subscribePhone(subscription: PushSubscriptionJSON) {
    return this.request<null>("/v1/push-subscriptions", {
      method: "POST",
      body: JSON.stringify(subscription),
    });
  }
  betWindows() {
    return this.request<{ data: DayWindowView[] }>("/v1/bet-windows");
  }
  ruleHealth() {
    return this.request<{ data: RuleHealthView[] }>("/v1/rule-health");
  }
  recommendations(sport: DashboardSport = "NBA") {
    return this.request<{ data: RecommendationView[] }>(
      sportPath("/v1/recommendations", sport),
    );
  }
  dashboard(sport: DashboardSport | "ALL" = "NBA") {
    return this.request<DashboardSnapshot>(
      `/v1/dashboard?sport=${encodeURIComponent(sport)}`,
    );
  }
  analytics(sport: DashboardSport | "ALL" = "ALL") {
    return this.request<{ data: AnalyticsRow[] }>(
      `/v1/analytics?sport=${encodeURIComponent(sport)}`,
    );
  }
  ruleHistory(sport: string, rule: string) {
    return this.request<{ data: RuleHistory }>(
      `/v1/analytics/rule?sport=${encodeURIComponent(sport)}&rule=${encodeURIComponent(rule)}`,
    );
  }
  ledger(sport: DashboardSport = "NBA") {
    return this.request<{ data: Record<string, unknown>[] }>(
      sportPath("/v1/ledger", sport),
    );
  }
  trackedLedger(sport: DashboardSport = "NBA") {
    return this.request<{ data: Record<string, unknown>[] }>(
      sportPath("/v1/ledger/tracked", sport),
    );
  }
  health(sport: DashboardSport = "NBA") {
    return this.request<HealthView>(sportPath("/v1/health", sport));
  }
  books() {
    return this.request<{
      data: BookExposureView[];
      funding?: FundingPlanView;
    }>("/v1/books");
  }
  recordBookTransaction(input: {
    bookmaker: string;
    kind: "DEPOSIT" | "WITHDRAWAL" | "BONUS" | "RECONCILE" | "ADJUSTMENT";
    amountCents: number;
    note: string | null;
  }) {
    return this.request<{
      data: BookExposureView[];
      funding?: FundingPlanView;
    }>("/v1/books/transactions", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  setBookStatus(input: {
    bookmaker: string;
    status: "ACTIVE" | "LIMITED" | "CLOSED";
  }) {
    return this.request<{
      data: BookExposureView[];
      funding?: FundingPlanView;
    }>("/v1/books/status", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  backlog() {
    return this.request<{ data: BacklogItemView[] }>("/v1/backlog");
  }
  addBacklogItem(input: { title: string; note: string | null }) {
    return this.request<{ data: BacklogItemView[] }>("/v1/backlog", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  setBacklogStatus(input: { id: string; status: BacklogItemView["status"] }) {
    return this.request<{ data: BacklogItemView[] }>("/v1/backlog/status", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  pendingLineups() {
    return this.request<{ data: PendingLineupView[] }>("/v1/lineups/pending");
  }
  /** Correct the book or the price on a bet that is already logged. */
  amendBet(input: {
    sport: string;
    betId: string;
    bookmaker?: string;
    actualPrice?: number;
    note?: string;
  }) {
    return this.request<{
      status: string;
      bookmaker: string;
      price: number;
      profitCents: number | null;
    }>("/v1/bets/amend", { method: "POST", body: JSON.stringify(input) });
  }
  /** Tick off a voided bet whose stake came back at the book. */
  confirmRefund(betId: string) {
    return this.request<{ betId: string; confirmedAt: string }>(
      "/v1/bets/refund-confirmed",
      { method: "POST", body: JSON.stringify({ betId }) },
    );
  }
  skipRecommendation(signalId: string) {
    return this.request<Record<string, unknown>>("/v1/bets/skip", {
      method: "POST",
      body: JSON.stringify({ signalId }),
    });
  }
  placeBet(input: {
    signalId: string;
    bookmaker: string;
    actualLine: number | null;
    actualPrice: number;
    stakeCents: number;
    allowNonLive?: boolean;
  }) {
    return this.request<Record<string, unknown>>("/v1/bets/place", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }
  confirmLineup(input: {
    gameId: string;
    playerId: string;
    status: "CONFIRMED_STARTER" | "CONFIRMED_NON_STARTER";
    source: string;
  }) {
    return this.request<Record<string, unknown>>("/v1/lineups/confirm", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  async exportPlaced(sport: DashboardSport): Promise<void> {
    const token = this.token();
    const response = await fetch(
      `${this.baseUrl}${sportPath("/v1/exports/placed.csv", sport)}`,
      {
        headers: token === null ? {} : { Authorization: `Bearer ${token}` },
      },
    );
    if (response.status === 401) this.onUnauthorized();
    if (!response.ok) throw new Error(`Export failed with ${response.status}`);
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${sport.toLowerCase()}-placed.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
